import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Input } from '@/components/ui/input'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Typography } from '@/components/ui/typography'
import { ComboboxListbox, useComboboxListbox } from '@/components/form/combobox'
import {
  getErrorMessage,
  isRecord,
  parseAndFormatCurrency,
  parseCurrency,
  type NumericInput,
} from '@/lib/utils'
import { useAppForm } from '@/lib/form'
import {
  getOrderQuoteFinancials,
  getOrderQuotePricingAdjustmentControls,
  getOrderQuoteRelayFeeControls,
  getOrderQuoteTipControls,
} from '@/lib/order-quote'
import {
  useApplyCartDiscountMutation,
  useCartDiscountSuggestions,
  useApplyCartTipMutation,
  useIsCartMutating,
  useUpdateCartMutation,
} from '@/lib/services/carts/carts.query'
import type {
  CartAdjustmentType,
  CartDiscountData,
  CartDiscountSuggestion,
  PosCart,
} from '@/lib/services/carts/carts.types'
import { useDebounce } from '@uidotdev/usehooks'
import { Check, CircleDollarSign, DollarSign, Pencil, Percent, Plus, Tag, X } from 'lucide-react'
import { useRef, useState, type ChangeEvent, type FocusEvent } from 'react'
import { toast } from 'sonner'

const DEFAULT_RELAY_FEE_AMOUNT = 2
const RELAY_FEE_AMOUNT_STORAGE_KEY = 'tukios_flowers:pos_relay_fee_amounts:v1'

/** A discount attached to the cart (DiscountResource). */
type CartDiscount = NonNullable<PosCart['discounts']>[number]

type DiscountFormType = 'code' | CartAdjustmentType

interface DiscountFormValues {
  discountAmount: string
  discountCode: string
  discountType: DiscountFormType
}

interface TipFormValues {
  tipAmount: string
  tipType: CartAdjustmentType
  /** The driver's share of a delivery tip; the designer gets the rest. */
  driverPercent: number
}

interface RelayFeeFormValues {
  relayFeeAmount: string
}

const isCartAdjustmentType = (value: string): value is CartAdjustmentType =>
  value === 'percent' || value === 'amount'

const isDiscountFormType = (value: string): value is DiscountFormType =>
  value === 'code' || isCartAdjustmentType(value)

function getRelayFeeAmountKey(organizationId: number | undefined) {
  return String(organizationId ?? 'unknown')
}

function readRelayFeeAmountMemory(): Record<string, unknown> {
  if (typeof globalThis.localStorage === 'undefined') {
    return {}
  }

  try {
    const parsed: unknown = JSON.parse(
      globalThis.localStorage.getItem(RELAY_FEE_AMOUNT_STORAGE_KEY) ?? '{}'
    )

    return isRecord(parsed) && !Array.isArray(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

function getRememberedRelayFeeAmount(organizationId: number | undefined) {
  const rememberedAmount = readRelayFeeAmountMemory()[getRelayFeeAmountKey(organizationId)]

  if (typeof rememberedAmount !== 'string') {
    return DEFAULT_RELAY_FEE_AMOUNT.toFixed(2)
  }

  const normalizedAmount = parseCurrency(rememberedAmount)

  return normalizedAmount > 0 && normalizedAmount <= 9999.99
    ? rememberedAmount
    : DEFAULT_RELAY_FEE_AMOUNT.toFixed(2)
}

function rememberRelayFeeAmount({
  organizationId,
  amount,
}: {
  organizationId: number
  amount: number
}) {
  if (typeof globalThis.localStorage === 'undefined') {
    return
  }

  const normalizedAmount = Math.round(parseCurrency(amount ?? 0) * 100) / 100

  if (!Number.isFinite(normalizedAmount) || normalizedAmount <= 0 || normalizedAmount > 9999.99) {
    return
  }

  const nextMemory = {
    ...readRelayFeeAmountMemory(),
    [getRelayFeeAmountKey(organizationId)]: normalizedAmount.toFixed(2),
  }

  try {
    globalThis.localStorage.setItem(RELAY_FEE_AMOUNT_STORAGE_KEY, JSON.stringify(nextMemory))
  } catch {
    // Local memory is a convenience only; failing to persist should not block order entry.
  }
}

export const getDiscountAmountValue = (discount: CartDiscount | null | undefined): string => {
  if (!discount) {
    return ''
  }

  return discount.type === 'percent'
    ? String(discount.value ?? '')
    : (parseAndFormatCurrency(discount.value)?.replace(/[$,-]/g, '') ?? '')
}

export const getDiscountTypeValue = (
  discount: CartDiscount | null | undefined
): DiscountFormType =>
  discount?.code ? 'code' : discount?.type === 'percent' ? 'percent' : 'amount'

export const getDiscountFormDefaults = (
  discount: CartDiscount | null | undefined
): DiscountFormValues => ({
  discountAmount: getDiscountAmountValue(discount),
  discountCode: discount?.code ?? '',
  discountType: getDiscountTypeValue(discount),
})

export const getTipFormDefaults = (cart: PosCart | null): TipFormValues => {
  const quoteTipAmount = getOrderQuoteFinancials(cart).tipAmount
  const tipControls = getOrderQuoteTipControls(cart)
  const hasQuoteTipAmount = Number(quoteTipAmount ?? 0) > 0

  return {
    tipAmount:
      tipControls.type === 'percent'
        ? String(tipControls.value ?? '')
        : hasQuoteTipAmount
          ? (parseAndFormatCurrency(quoteTipAmount)?.replace(/[$,-]/g, '') ?? '')
          : '',
    tipType:
      tipControls.type === 'percent' || tipControls.type === 'amount'
        ? tipControls.type
        : hasQuoteTipAmount
          ? 'amount'
          : 'percent',
    // Only read while the split is offered, when the quote always sends the share.
    driverPercent: tipControls.driverPercent ?? 0,
  }
}

export const getRelayFeeFormDefaults = (cart: PosCart | null): RelayFeeFormValues => {
  const quoteRelayFee = getOrderQuoteFinancials(cart).relayFee

  return {
    relayFeeAmount:
      Number(quoteRelayFee ?? 0) > 0
        ? (parseAndFormatCurrency(quoteRelayFee)?.replace(/[$,-]/g, '') ??
          DEFAULT_RELAY_FEE_AMOUNT.toFixed(2))
        : getRememberedRelayFeeAmount(cart?.organization_id),
  }
}

const toNumber = (value: NumericInput) => {
  const parsed = parseCurrency(value ?? 0)
  return Number.isFinite(parsed) ? parsed : 0
}

const roundToCents = (value: number) => Math.round(value * 100) / 100

const normalizeCodeInput = (value: string | null | undefined) =>
  String(value ?? '')
    .trim()
    .toUpperCase()

export const getDiscountSubmitValue = ({
  discountType,
  discountAmount,
}: {
  discountType: DiscountFormType
  discountAmount: NumericInput
}): number => {
  const discountValue = Math.max(toNumber(discountAmount), 0)

  if (discountType === 'percent') {
    return roundToCents(Math.min(discountValue, 100))
  }

  return roundToCents(discountValue)
}

export const getDiscountMutationPayload = ({
  discountAmount,
  discountCode,
  discountType,
}: Partial<DiscountFormValues> = {}): CartDiscountData | null => {
  if (discountType === 'code') {
    const code = normalizeCodeInput(discountCode)

    return code ? { code } : null
  }

  if (discountType === 'percent' || discountType === 'amount') {
    if (!String(discountAmount ?? '').trim()) {
      return null
    }

    return {
      type: discountType,
      value: getDiscountSubmitValue({
        discountType,
        discountAmount,
      }),
    }
  }

  return null
}

const getAppliedDiscountFormKey = (cart: PosCart | null, discount: CartDiscount | null) =>
  `${cart?.id ?? 'new'}-${discount?.id ?? 'new'}-${discount?.type ?? 'amount'}-${discount?.value ?? ''}`

const getAppliedTipFormKey = (cart: PosCart | null) => {
  const quoteTipAmount = getOrderQuoteFinancials(cart).tipAmount ?? 0
  const tipControls = getOrderQuoteTipControls(cart)

  return `${cart?.id ?? 'new'}-${tipControls.type ?? 'amount'}-${tipControls.value ?? ''}-${quoteTipAmount}-${tipControls.splitAvailable ? tipControls.driverPercent : 'unsplit'}`
}

const getAppliedRelayFeeFormKey = (cart: PosCart | null) => {
  const quoteRelayFee = getOrderQuoteFinancials(cart).relayFee ?? 0
  const relayFeeControls = getOrderQuoteRelayFeeControls(cart)

  return `${cart?.id ?? 'new'}-${relayFeeControls.enabled ? 'enabled' : 'disabled'}-${quoteRelayFee}`
}

const getAppliedTipAmount = (cart: PosCart | null) =>
  Number(getOrderQuoteFinancials(cart).tipAmount ?? 0)

const getAppliedDiscountAmount = (cart: PosCart | null) =>
  Math.abs(Number(getOrderQuoteFinancials(cart).adjustmentsTotal ?? 0))

const getAppliedTipLabel = (cart: PosCart | null) => {
  const tipControls = getOrderQuoteTipControls(cart)

  if (tipControls.type === 'percent' && Number(tipControls.value ?? 0) > 0) {
    return `${Number(tipControls.value)}%`
  }

  return parseAndFormatCurrency(getAppliedTipAmount(cart))
}

const getAppliedRelayFeeAmount = (cart: PosCart | null) =>
  Number(getOrderQuoteFinancials(cart).relayFee ?? 0)

const getAppliedRelayFeeLabel = (cart: PosCart | null) =>
  parseAndFormatCurrency(getAppliedRelayFeeAmount(cart))

function formatDiscountSuggestionUsage(discount: CartDiscountSuggestion) {
  const usageCount = Number(discount?.usage_count ?? 0)

  return usageCount === 1 ? '1 use' : `${usageCount} uses`
}

interface DiscountCodeSuggestionComboboxProps {
  cartId: number | undefined
  disabled: boolean
  enabled: boolean
  id: string
  onValueChange: (value: string) => void
  value: string
}

function DiscountCodeSuggestionCombobox({
  cartId,
  disabled,
  enabled,
  id,
  onValueChange,
  value,
}: DiscountCodeSuggestionComboboxProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const inputValue = value ?? ''
  const debouncedSearch = useDebounce(inputValue, 200)
  const suggestionsQuery = useCartDiscountSuggestions({
    cartId,
    search: debouncedSearch,
    enabled: Boolean(cartId) && Boolean(enabled) && !disabled,
  })
  const suggestions = suggestionsQuery.data?.data ?? []
  const hasSuggestions = suggestions.length > 0
  const combobox = useComboboxListbox({
    id: id ? `${id}-suggestions` : undefined,
    optionCount: suggestions.length,
  })

  const openSuggestions = () => {
    if (disabled) {
      return
    }

    if (hasSuggestions) {
      combobox.openWithFirstOption()
      return
    }

    combobox.openWithoutActiveOption()
  }

  const closeSuggestions = () => {
    combobox.close()
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    onValueChange(normalizeCodeInput(event.target.value))
    combobox.openWithoutActiveOption()
  }

  const handleInputBlur = (event: FocusEvent<HTMLInputElement>) => {
    if (event.relatedTarget instanceof Node && contentRef.current?.contains(event.relatedTarget)) {
      return
    }

    closeSuggestions()
  }

  const handleSuggestionSelect = (discount: CartDiscountSuggestion | undefined) => {
    onValueChange(normalizeCodeInput(discount?.code))
    closeSuggestions()
    inputRef.current?.focus()
  }

  const renderSuggestion = (discount: CartDiscountSuggestion) => (
    <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
      <div className="min-w-0">
        <Typography variant="label" className="block truncate">
          {discount.code}
        </Typography>
        {discount.name ? (
          <Typography variant="caption" className="block truncate">
            {discount.name}
          </Typography>
        ) : null}
      </div>
      <Badge variant="secondary" size="sm" className="shrink-0">
        {formatDiscountSuggestionUsage(discount)}
      </Badge>
    </div>
  )

  return (
    <Popover
      open={combobox.isOpen}
      onOpenChange={(nextOpen) => (nextOpen ? openSuggestions() : closeSuggestions())}
    >
      <PopoverAnchor asChild>
        <Input
          ref={inputRef}
          id={id}
          aria-activedescendant={combobox.activeOptionId}
          aria-autocomplete="list"
          aria-controls={combobox.isOpen ? combobox.listboxId : undefined}
          aria-expanded={combobox.isOpen}
          aria-label="Discount code"
          autoComplete="one-time-code"
          disabled={disabled}
          placeholder="SAVE10"
          role="combobox"
          value={inputValue}
          onBlur={handleInputBlur}
          onChange={handleInputChange}
          onFocus={openSuggestions}
          onKeyDown={(event) =>
            combobox.handleKeyDown(event, (index) => handleSuggestionSelect(suggestions[index]))
          }
          onMouseDown={openSuggestions}
        />
      </PopoverAnchor>
      <PopoverContent
        ref={contentRef}
        align="start"
        size="trigger"
        padding="none"
        elevation="subtle"
        onOpenAutoFocus={(event) => event.preventDefault()}
        onInteractOutside={(event) => {
          if (event.target === inputRef.current) {
            event.preventDefault()
          }
        }}
      >
        <ComboboxListbox
          items={suggestions}
          listboxId={combobox.listboxId}
          activeIndex={combobox.activeIndex}
          selectedOptionKey={inputValue}
          isLoading={suggestionsQuery.isLoading || (suggestionsQuery.isFetching && !hasSuggestions)}
          emptyMessage={inputValue ? 'No matching discount codes.' : 'No available discount codes.'}
          getOptionKey={(discount) => discount.code}
          renderOption={renderSuggestion}
          onActivateOption={combobox.activateOption}
          onSelectOption={handleSuggestionSelect}
        />
      </PopoverContent>
    </Popover>
  )
}

function focusAmountInput(id: string) {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const input = document.getElementById(id)

      if (!(input instanceof HTMLInputElement)) {
        return
      }

      input.focus()
      input.select()
    })
  })
}

function focusAmountInputAfterTypeChange(id: string) {
  window.setTimeout(() => {
    const input = document.getElementById(id)

    if (!(input instanceof HTMLInputElement)) {
      return
    }

    input.focus()
    input.select()
  }, 0)
}

interface POSCartDiscountFormProps {
  cart: PosCart | null
  appliedDiscount: CartDiscount | null
  isCartDiscountOpen: boolean
  setIsCartDiscountOpen: (isOpen: boolean) => void
}

function POSCartDiscountForm({
  cart,
  appliedDiscount,
  isCartDiscountOpen,
  setIsCartDiscountOpen,
}: POSCartDiscountFormProps) {
  const applyDiscountMutation = useApplyCartDiscountMutation()
  const isCartMutating = useIsCartMutating()
  const pricingAdjustmentControls = getOrderQuotePricingAdjustmentControls(cart)
  const isDiscountActionDisabled =
    isCartMutating > 0 || !cart?.id || !pricingAdjustmentControls.editable
  const form = useAppForm({
    defaultValues: getDiscountFormDefaults(appliedDiscount),
  })

  const handleDiscountOpenChange = (isOpen: boolean) => {
    setIsCartDiscountOpen(isOpen)

    if (isOpen) {
      focusAmountInput(
        form.getFieldValue('discountType') === 'code'
          ? 'pos-cart-discount-code'
          : 'pos-cart-discount-amount'
      )
    }
  }

  const handleDiscountTypeChange = (value: string) => {
    if (!isDiscountFormType(value)) {
      return
    }

    form.setFieldValue('discountType', value)
    form.setFieldValue('discountAmount', '')
    form.setFieldValue('discountCode', '')

    if (value === 'code') {
      focusAmountInputAfterTypeChange('pos-cart-discount-code')
    } else {
      focusAmountInputAfterTypeChange('pos-cart-discount-amount')
    }
  }

  const handleApplyDiscount = async () => {
    if (!cart?.id) return

    const discountType = form.getFieldValue('discountType')
    const payload = getDiscountMutationPayload({
      discountAmount: form.getFieldValue('discountAmount'),
      discountCode: form.getFieldValue('discountCode'),
      discountType,
    })

    if (!payload) {
      return
    }

    try {
      await applyDiscountMutation.mutateAsync({
        cartId: cart.id,
        data: payload,
      })
      form.reset()
      setIsCartDiscountOpen(false)
    } catch (error) {
      toast.error('Failed to update discount', {
        description: getErrorMessage(error) || 'Please try again',
      })
    }
  }

  const handleCancelEdit = () => {
    form.reset(getDiscountFormDefaults(appliedDiscount))
    setIsCartDiscountOpen(false)
  }

  const handleCancelAppliedDiscount = async () => {
    if (!cart?.id) return

    try {
      await applyDiscountMutation.mutateAsync({
        cartId: cart.id,
        data: appliedDiscount?.code
          ? { clear_discount_code: true }
          : {
              type:
                appliedDiscount && isCartAdjustmentType(appliedDiscount.type)
                  ? appliedDiscount.type
                  : 'amount',
              value: 0,
            },
      })
      form.reset()
      setIsCartDiscountOpen(false)
    } catch (error) {
      toast.error('Failed to update discount', {
        description: getErrorMessage(error) || 'Please try again',
      })
    }
  }

  return (
    <Collapsible open={isCartDiscountOpen} onOpenChange={handleDiscountOpenChange}>
      <form.AppForm>
        <div className="mb-2 space-y-2" data-testid="pos-cart-discount">
          <>
            <div
              className="flex min-h-8 items-end justify-between gap-2"
              data-testid="pos-cart-discount-row"
            >
              <Typography variant="label" tone="muted">
                Discount
              </Typography>
              <div
                className="flex translate-y-1 items-end gap-2"
                data-testid="pos-cart-discount-actions"
              >
                {!isCartDiscountOpen && (
                  <>
                    {appliedDiscount ? (
                      <div
                        className="flex items-center gap-2"
                        data-testid="pos-cart-discount-controls"
                      >
                        {appliedDiscount?.name ? (
                          <Badge variant="secondary" size="sm" className="h-7 px-2">
                            {appliedDiscount.name}
                          </Badge>
                        ) : null}
                        <CollapsibleTrigger asChild>
                          <Button
                            type="button"
                            variant="ghostMuted"
                            size="icon"
                            disabled={isDiscountActionDisabled}
                            aria-label="Edit Discount"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                        <Button
                          type="button"
                          variant="ghostMuted"
                          size="icon"
                          onClick={handleCancelAppliedDiscount}
                          disabled={isDiscountActionDisabled}
                          aria-label="Clear Discount"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <CollapsibleTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="px-4 has-[>svg]:px-3"
                          disabled={isDiscountActionDisabled}
                        >
                          <Plus className="h-4 w-4" />
                          Add
                        </Button>
                      </CollapsibleTrigger>
                    )}
                  </>
                )}
              </div>
            </div>

            <CollapsibleContent className="space-y-2">
              <form.Subscribe selector={(state) => state.values.discountType}>
                {(discountType) => (
                  <Tabs
                    value={discountType}
                    onValueChange={handleDiscountTypeChange}
                    className="gap-2"
                  >
                    <TabsList layout="fill">
                      <TabsTrigger
                        value="percent"
                        className="gap-1.5"
                        disabled={isDiscountActionDisabled}
                      >
                        <Percent className="h-4 w-4" />
                        Percent
                      </TabsTrigger>
                      <TabsTrigger
                        value="amount"
                        className="gap-1.5"
                        disabled={isDiscountActionDisabled}
                      >
                        <DollarSign className="h-4 w-4" />
                        Amount
                      </TabsTrigger>
                      {/* A change to an issued order never redeems a promotion code. */}
                      {cart?.order_change ? null : (
                        <TabsTrigger
                          value="code"
                          className="gap-1.5"
                          disabled={isDiscountActionDisabled}
                        >
                          <Tag className="h-4 w-4" />
                          Code
                        </TabsTrigger>
                      )}
                    </TabsList>

                    <TabsContent value="percent" className="mt-0">
                      <form.AppField name="discountAmount">
                        {(field) => (
                          <field.PercentInput
                            id="pos-cart-discount-amount"
                            label=""
                            placeholder="0"
                            disabled={isDiscountActionDisabled}
                          />
                        )}
                      </form.AppField>
                    </TabsContent>

                    <TabsContent value="amount" className="mt-0">
                      <form.AppField name="discountAmount">
                        {(field) => (
                          <field.MoneyInput
                            id="pos-cart-discount-amount"
                            label=""
                            placeholder="0.00"
                            showFreeBadge={false}
                            disabled={isDiscountActionDisabled}
                          />
                        )}
                      </form.AppField>
                    </TabsContent>

                    <TabsContent value="code" className="mt-0">
                      <form.AppField name="discountCode">
                        {(field) => (
                          <DiscountCodeSuggestionCombobox
                            cartId={cart?.id}
                            id="pos-cart-discount-code"
                            disabled={isDiscountActionDisabled}
                            enabled={discountType === 'code'}
                            value={field.state.value}
                            onValueChange={field.handleChange}
                          />
                        )}
                      </form.AppField>
                    </TabsContent>
                  </Tabs>
                )}
              </form.Subscribe>
              <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border/60">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="w-full"
                  onClick={handleCancelEdit}
                  disabled={isDiscountActionDisabled}
                >
                  Cancel
                </Button>
                <form.Subscribe
                  selector={(state) =>
                    [
                      state.values.discountAmount,
                      state.values.discountCode,
                      state.values.discountType,
                    ] as const
                  }
                >
                  {([discountAmount, discountCode, discountType]) => (
                    <Button
                      type="button"
                      size="sm"
                      variant="default"
                      className="w-full"
                      onClick={handleApplyDiscount}
                      disabled={
                        isDiscountActionDisabled ||
                        !getDiscountMutationPayload({
                          discountAmount,
                          discountCode,
                          discountType,
                        })
                      }
                    >
                      <Check className="h-4 w-4" />
                      Apply
                    </Button>
                  )}
                </form.Subscribe>
              </div>
            </CollapsibleContent>
          </>
        </div>
      </form.AppForm>
    </Collapsible>
  )
}

interface POSCartTipFormProps {
  cart: PosCart | null
  isCartTipOpen: boolean
  setIsCartTipOpen: (isOpen: boolean) => void
}

function POSCartTipForm({ cart, isCartTipOpen, setIsCartTipOpen }: POSCartTipFormProps) {
  const applyTipMutation = useApplyCartTipMutation()
  const isCartMutating = useIsCartMutating()
  const tipControls = getOrderQuoteTipControls(cart)
  const isTipActionDisabled = isCartMutating > 0 || !cart?.id || !tipControls.editable
  const form = useAppForm({
    defaultValues: getTipFormDefaults(cart),
  })
  const tipAmount = getAppliedTipAmount(cart)

  const handleTipOpenChange = (isOpen: boolean) => {
    setIsCartTipOpen(isOpen)

    if (isOpen) {
      focusAmountInput('pos-cart-tip-amount')
    }
  }

  const handleTipTypeChange = (value: string) => {
    if (!isCartAdjustmentType(value)) {
      return
    }

    form.setFieldValue('tipType', value)
    form.setFieldValue('tipAmount', '')
    focusAmountInputAfterTypeChange('pos-cart-tip-amount')
  }

  const handleApplyTip = async () => {
    if (!cart?.id) return

    const tipType = form.getFieldValue('tipType')
    const tipAmountValue = form.getFieldValue('tipAmount')
    // A share staff moved, even back to where it started (an even split on an unsplit order is a choice). An
    // untouched slider records nothing, so an order placed before the split stays unsplit.
    const driverPercent = form.getFieldMeta('driverPercent')?.isDirty
      ? form.getFieldValue('driverPercent')
      : null
    const value =
      tipType === 'percent'
        ? roundToCents(Math.min(Math.max(toNumber(tipAmountValue), 0), 100))
        : roundToCents(Math.min(Math.max(toNumber(tipAmountValue), 0), 9999.99))

    try {
      await applyTipMutation.mutateAsync({
        cartId: cart.id,
        type: tipType,
        value,
        ...(tipControls.splitAvailable && driverPercent !== null
          ? { driver_percent: driverPercent }
          : {}),
      })
      form.reset()
      setIsCartTipOpen(false)
    } catch (error) {
      toast.error('Failed to update tip', {
        description: getErrorMessage(error) || 'Please try again',
      })
    }
  }

  const handleCancelEdit = () => {
    form.reset(getTipFormDefaults(cart))
    setIsCartTipOpen(false)
  }

  const handleClearTip = async () => {
    if (!cart?.id) return

    try {
      await applyTipMutation.mutateAsync({
        cartId: cart.id,
        type: 'amount',
        value: 0,
      })
      form.reset()
      setIsCartTipOpen(false)
    } catch (error) {
      toast.error('Failed to update tip', {
        description: getErrorMessage(error) || 'Please try again',
      })
    }
  }

  return (
    <Collapsible open={isCartTipOpen} onOpenChange={handleTipOpenChange}>
      <form.AppForm>
        <div className="mb-2 space-y-2" data-testid="pos-cart-tip">
          <div className="flex min-h-8 items-end justify-between gap-2">
            <Typography variant="label" tone="muted">
              Tip
            </Typography>
            <div className="flex translate-y-1 items-end gap-2">
              {!isCartTipOpen && (
                <>
                  {tipAmount > 0 ? (
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" size="sm" className="h-7 px-2">
                        {getAppliedTipLabel(cart)}
                      </Badge>
                      {tipControls.splitAvailable && tipControls.splitRecorded ? (
                        <Badge variant="secondary" size="sm" className="h-7 px-2">
                          Driver {tipControls.driverPercent}%
                        </Badge>
                      ) : null}
                      <CollapsibleTrigger asChild>
                        <Button
                          type="button"
                          variant="ghostMuted"
                          size="icon"
                          disabled={isTipActionDisabled}
                          aria-label="Edit Tip"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </CollapsibleTrigger>
                      <Button
                        type="button"
                        variant="ghostMuted"
                        size="icon"
                        onClick={handleClearTip}
                        disabled={isTipActionDisabled}
                        aria-label="Clear Tip"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <CollapsibleTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="px-4 has-[>svg]:px-3"
                        disabled={isTipActionDisabled}
                      >
                        <Plus className="h-4 w-4" />
                        Add
                      </Button>
                    </CollapsibleTrigger>
                  )}
                </>
              )}
            </div>
          </div>

          <CollapsibleContent className="space-y-2">
            <form.Subscribe selector={(state) => state.values.tipType}>
              {(tipType) => (
                <Tabs value={tipType} onValueChange={handleTipTypeChange} className="gap-2">
                  <TabsList layout="fill">
                    <TabsTrigger value="percent" className="gap-1.5" disabled={isTipActionDisabled}>
                      <Percent className="h-4 w-4" />
                      Percent
                    </TabsTrigger>
                    <TabsTrigger value="amount" className="gap-1.5" disabled={isTipActionDisabled}>
                      <DollarSign className="h-4 w-4" />
                      Amount
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="percent" className="mt-0">
                    <div className="grid grid-cols-3 gap-2 pb-2">
                      {[10, 15, 20].map((preset) => (
                        <Button
                          key={preset}
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={isTipActionDisabled}
                          onClick={() => form.setFieldValue('tipAmount', String(preset))}
                        >
                          {preset}%
                        </Button>
                      ))}
                    </div>
                    <form.AppField name="tipAmount">
                      {(field) => (
                        <field.PercentInput
                          id="pos-cart-tip-amount"
                          label=""
                          placeholder="0"
                          disabled={isTipActionDisabled}
                        />
                      )}
                    </form.AppField>
                  </TabsContent>

                  <TabsContent value="amount" className="mt-0">
                    <form.AppField name="tipAmount">
                      {(field) => (
                        <field.MoneyInput
                          id="pos-cart-tip-amount"
                          label=""
                          placeholder="0.00"
                          showFreeBadge={false}
                          disabled={isTipActionDisabled}
                        />
                      )}
                    </form.AppField>
                  </TabsContent>
                </Tabs>
              )}
            </form.Subscribe>
            {tipControls.splitAvailable ? (
              <form.AppField name="driverPercent">
                {(field) => (
                  <div className="space-y-1 pb-2">
                    <field.SliderInput
                      label="Driver's share"
                      unit="%"
                      min={0}
                      max={100}
                      step={5}
                      disabled={isTipActionDisabled}
                      thumbAriaLabel="Driver's share of the tip"
                      thumbAriaValueText={`Driver ${field.state.value}%, designer ${100 - field.state.value}%`}
                    />
                    <Typography variant="caption" tone="muted">
                      The designer gets {100 - field.state.value}%.
                    </Typography>
                  </div>
                )}
              </form.AppField>
            ) : null}
            {tipControls.locked ? (
              <Typography variant="label" tone="muted">
                Tips are locked after payment starts.
              </Typography>
            ) : null}
            <div className="grid grid-cols-2 gap-2 border-t border-border/60 pt-2">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="w-full"
                onClick={handleCancelEdit}
                disabled={isTipActionDisabled}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                variant="default"
                className="w-full"
                onClick={handleApplyTip}
                disabled={isTipActionDisabled}
              >
                <Check className="h-4 w-4" />
                Apply
              </Button>
            </div>
          </CollapsibleContent>
        </div>
      </form.AppForm>
    </Collapsible>
  )
}

function POSCartRelayFeeForm({ cart }: { cart: PosCart | null }) {
  const updateCartMutation = useUpdateCartMutation()
  const isCartMutating = useIsCartMutating()
  const [isCartRelayFeeOpen, setIsCartRelayFeeOpen] = useState(false)
  const relayFeeControls = getOrderQuoteRelayFeeControls(cart)
  const isRelayFeeActionDisabled =
    isCartMutating > 0 || updateCartMutation.isPending || !cart?.id || !relayFeeControls.editable
  const form = useAppForm({
    defaultValues: getRelayFeeFormDefaults(cart),
  })
  const relayFeeAmount = getAppliedRelayFeeAmount(cart)
  const hasAppliedRelayFee = Boolean(relayFeeControls.enabled && relayFeeAmount > 0)

  const handleRelayFeeOpenChange = (isOpen: boolean) => {
    setIsCartRelayFeeOpen(isOpen)

    if (isOpen) {
      focusAmountInput('pos-cart-relay-fee-amount')
    }
  }

  const handleApplyRelayFee = async () => {
    if (!cart?.id) return

    const value = roundToCents(toNumber(form.getFieldValue('relayFeeAmount')))

    if (value <= 0 || value > 9999.99) {
      toast.error('Enter a valid relay fee', {
        description: 'Use an amount from $0.01 to $9,999.99.',
      })
      return
    }

    try {
      await updateCartMutation.mutateAsync({
        cartId: cart.id,
        data: {
          relay_fee_enabled: true,
          relay_fee: value,
        },
      })
      rememberRelayFeeAmount({ organizationId: cart.organization_id, amount: value })
      form.reset()
      setIsCartRelayFeeOpen(false)
    } catch (error) {
      toast.error('Failed to update relay fee', {
        description: getErrorMessage(error) || 'Please try again',
      })
    }
  }

  const handleCancelEdit = () => {
    form.reset(getRelayFeeFormDefaults(cart))
    setIsCartRelayFeeOpen(false)
  }

  const handleClearRelayFee = async () => {
    if (!cart?.id) return

    try {
      await updateCartMutation.mutateAsync({
        cartId: cart.id,
        data: {
          relay_fee_enabled: false,
          relay_fee: 0,
        },
      })
      form.reset()
      setIsCartRelayFeeOpen(false)
    } catch (error) {
      toast.error('Failed to update relay fee', {
        description: getErrorMessage(error) || 'Please try again',
      })
    }
  }

  return (
    <Collapsible open={isCartRelayFeeOpen} onOpenChange={handleRelayFeeOpenChange}>
      <form.AppForm>
        <div className="mb-2 space-y-2" data-testid="pos-cart-relay-fee">
          <div className="flex min-h-8 items-end justify-between gap-2">
            <Typography variant="label" tone="muted">
              Relay Fee
            </Typography>
            <div className="flex translate-y-1 items-end gap-2">
              {!isCartRelayFeeOpen ? (
                hasAppliedRelayFee ? (
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" size="sm" className="h-7 px-2">
                      {getAppliedRelayFeeLabel(cart)}
                    </Badge>
                    <CollapsibleTrigger asChild>
                      <Button
                        type="button"
                        variant="ghostMuted"
                        size="icon"
                        disabled={isRelayFeeActionDisabled}
                        aria-label="Edit Relay Fee"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </CollapsibleTrigger>
                    <Button
                      type="button"
                      variant="ghostMuted"
                      size="icon"
                      onClick={handleClearRelayFee}
                      disabled={isRelayFeeActionDisabled}
                      aria-label="Clear Relay Fee"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <CollapsibleTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="px-4 has-[>svg]:px-3"
                      disabled={isRelayFeeActionDisabled}
                    >
                      <Plus className="h-4 w-4" />
                      Add
                    </Button>
                  </CollapsibleTrigger>
                )
              ) : null}
            </div>
          </div>

          <CollapsibleContent className="space-y-2">
            <div
              data-testid="relay-fee-retention-note"
              className="flex items-start gap-2 rounded-md bg-status-success-background px-2.5 py-1.5 text-status-success-foreground ring-1 ring-inset ring-status-success-border dark:text-status-success-foreground"
            >
              <CircleDollarSign className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              <Typography variant="label" tone="inherit" className="min-w-0">
                <span className="font-semibold">Kept by your shop</span>
                <span className="text-status-success-foreground dark:text-status-success-foreground">
                  {' '}
                  and not sent to Flower Shop Network.
                </span>
              </Typography>
            </div>
            <form.AppField name="relayFeeAmount">
              {(field) => (
                <field.MoneyInput
                  id="pos-cart-relay-fee-amount"
                  label=""
                  aria-label="Relay fee amount"
                  placeholder="0.00"
                  showFreeBadge={false}
                  disabled={isRelayFeeActionDisabled}
                />
              )}
            </form.AppField>
            {relayFeeControls.locked ? (
              <Typography variant="label" tone="muted">
                Relay fees are locked after payment starts.
              </Typography>
            ) : null}
            <div className="grid grid-cols-2 gap-2 border-t border-border/60 pt-2">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="w-full"
                onClick={handleCancelEdit}
                disabled={isRelayFeeActionDisabled}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                variant="default"
                className="w-full"
                onClick={handleApplyRelayFee}
                disabled={isRelayFeeActionDisabled}
              >
                <Check className="h-4 w-4" />
                Apply
              </Button>
            </div>
          </CollapsibleContent>
          {relayFeeControls.enabled && relayFeeAmount <= 0 ? (
            <Typography variant="label" tone="destructive" align="right" className="block pb-1">
              Enter an amount greater than $0.00 before taking payment.
            </Typography>
          ) : null}
        </div>
      </form.AppForm>
    </Collapsible>
  )
}

// The quote's discounts (CheckoutQuoteService) are untyped; one short of its minimum carries the message.
function getMinimumPurchaseMessage(cart: PosCart | null) {
  const quoteDiscounts = cart?.order_quote?.discounts
  const discounts: unknown[] = Array.isArray(quoteDiscounts) ? quoteDiscounts : []
  const unmetDiscount = discounts.find(
    (discount) => isRecord(discount) && discount.minimum_met === false
  )
  const message = isRecord(unmetDiscount) ? unmetDiscount.minimum_purchase_message : null

  return typeof message === 'string' ? message : null
}

interface POSCartDiscountProps {
  cart: PosCart | null
}

export function POSCartDiscount({ cart }: POSCartDiscountProps) {
  const [isCartDiscountOpen, setIsCartDiscountOpen] = useState(false)
  const [isCartTipOpen, setIsCartTipOpen] = useState(false)
  const minimumMessage = getMinimumPurchaseMessage(cart)
  const attachedDiscount = cart?.discounts?.[0]
  // An attached code stays removable even while it contributes no savings.
  const appliedDiscount =
    attachedDiscount && (attachedDiscount.code || getAppliedDiscountAmount(cart) > 0)
      ? attachedDiscount
      : null
  const relayFeeControls = getOrderQuoteRelayFeeControls(cart)
  // An order change starts with the order's tip and relay fee, and the server says whether its draft can change the
  // relay fee (editable); a refunded one stays as the refund left it, as a refunded delivery charge does.
  const showTip = !cart?.order_change?.tip_refunded
  const showRelayFee =
    !cart?.order_change?.relay_fee_refunded &&
    (relayFeeControls.available || relayFeeControls.enabled || relayFeeControls.editable)

  return (
    <div className="space-y-1">
      <POSCartDiscountForm
        key={getAppliedDiscountFormKey(cart, appliedDiscount)}
        cart={cart}
        appliedDiscount={appliedDiscount}
        isCartDiscountOpen={isCartDiscountOpen}
        setIsCartDiscountOpen={setIsCartDiscountOpen}
      />
      {minimumMessage ? (
        <Typography variant="description" tone="warning" role="status">
          {minimumMessage}
        </Typography>
      ) : null}
      {showTip ? (
        <POSCartTipForm
          key={getAppliedTipFormKey(cart)}
          cart={cart}
          isCartTipOpen={isCartTipOpen}
          setIsCartTipOpen={setIsCartTipOpen}
        />
      ) : null}
      {showRelayFee ? (
        <POSCartRelayFeeForm key={getAppliedRelayFeeFormKey(cart)} cart={cart} />
      ) : null}
    </div>
  )
}
